import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

const useWebSocket = (onMessage) => {
  const ws = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const reconnectTimeout = useRef(null);
  const reconnectAttempts = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 5;

  const connect = () => {
    // Only connect if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    // Get WebSocket URL from backend URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    if (!backendUrl) {
      console.error('REACT_APP_BACKEND_URL is not set');
      return;
    }
    const wsUrl = backendUrl.replace(/^http/, 'ws') + '/api/ws';

    try {
      ws.current = new WebSocket(wsUrl);

      ws.current.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        reconnectAttempts.current = 0;
      };

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('WebSocket message received:', data);
          
          // Call the provided callback with the message
          if (onMessage) {
            onMessage(data);
          }
          
          // Show toast notifications for updates
          if (data.type === 'claim_submitted') {
            toast.info(`New claim submitted by ${data.data.hospital_name}`, {
              description: `Patient: ${data.data.patient_name}`,
            });
          } else if (data.type === 'deposit_added') {
            toast.success(`Deposit added to ${data.data.hospital_name}`, {
              description: `Amount: $${data.data.amount.toFixed(2)}`,
            });
          } else if (data.type === 'family_balance_updated') {
            toast.info(`Family balance updated`, {
              description: `Family: ${data.data.family_id}`,
            });
          } else if (data.type === 'claim_status_changed') {
            toast.info(`Claim status changed`, {
              description: `Claim ID: ${data.data.claim_id}`,
            });
          } else if (data.type === 'user_suspended') {
            // Send the suspended user to the warning page (do not log them out)
            const currentUsername = localStorage.getItem('username');
            if (currentUsername && data.username === currentUsername) {
              const suspendMsg = data.message || 'Your account has been suspended due to suspicious activity.';
              localStorage.setItem('user_status', 'Suspended');
              localStorage.setItem('suspension_message', suspendMsg);
              toast.error('Account suspended. Redirecting…', { duration: 4000 });
              setTimeout(() => {
                window.location.href = '/suspended';
              }, 800);
            }
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.current.onclose = () => {
        console.log('WebSocket disconnected');
        setIsConnected(false);
        
        // Attempt to reconnect
        if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttempts.current += 1;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          console.log(`Reconnecting in ${delay}ms... (Attempt ${reconnectAttempts.current})`);
          
          reconnectTimeout.current = setTimeout(() => {
            connect();
          }, delay);
        }
      };
    } catch (error) {
      console.error('Error creating WebSocket:', error);
    }
  };

  const disconnect = () => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
    }
    
    if (ws.current) {
      ws.current.close();
      ws.current = null;
    }
    
    setIsConnected(false);
  };

  useEffect(() => {
    connect();

    return () => {
      disconnect();
    };
  }, []); // Empty dependency array - connect once on mount

  return { isConnected, disconnect, reconnect: connect };
};

export default useWebSocket;
